import type { DeOrderBookApp } from '@base/types/app'

export interface NavLink {
  path: string
  name: string
  active: boolean
  activeClass: string
  locale: string
  target?: string
  isExternal?: boolean
  isSoon?: boolean
}

export enum DOBApplets {
  APP = 'app',
  NFT = 'nft',
  BAZAAR = 'bazaar',
  EXPLORER = 'explorer',
  PRO = 'pro',
}

export const WALLETS = reactive([
  { label: 'MetaMask', id: 'metamask' },
  { label: 'Wallet Connect', id: 'walletconnect' },
  { label: 'Binance Chain Wallet', id: 'binanceWallet' },
])

export const UPDATE_DURATION = 15 * 1000 // 15 seconds
export const BLOCK_PER_MINUTE = 5
export const BLOCK_PER_DAY = 24 * 60 * BLOCK_PER_MINUTE
export const BLOCK_PER_YEAR = 365 * BLOCK_PER_DAY

export const ERROR_CODE = {
  REQUEST_PENDING: -32002,
  ACTION_REJECTED: 'ACTION_REJECTED',
  USER_REJECTED: 4001,
} as const

export const DOB_EXTERNAL_LINKS_MAP = {
  APP: {
    name: 'DeOrderBook',
    url: 'https://app.deorderbook.com',
  },
  BAZAAR: {
    name: 'Bazaar',
    url: 'https://bazaar.deorderbook.com',
  },
  EXPLORER: {
    name: 'Explorer',
    url: 'https://explorer.deorderbook.com',
  },
  STATS: {
    name: 'Stats',
    url: 'https://stats.deorderbook.com',
  },
  DOCS: {
    name: 'Docs',
    url: 'https://docs.deorderbook.com',
  },
  HOME: {
    name: 'DeOrderBook',
    url: 'https://deorderbook.com',
  },
  TERMS: {
    name: 'Terms of Service',
    url: '/Terms_of_Service.pdf',
  },
  PRIVACY: {
    name: 'Privacy Policy',
    url: '/Privacy_Policy.pdf',
  },
  DISCLAIMER: {
    name: 'Disclaimer',
    url: '/Disclaimer.pdf',
  },
  LICENSE: {
    name: 'License',
    url: 'https://github.com/DeOrderBook/v1-core/blob/main/LICENSE.md',
  },
  TWITTER: {
    name: 'Twitter',
    url: 'https://twitter.com/DeOrderBook',
  },
  DISCORD: {
    name: 'Discord',
    url: 'https://discord.gg/f5kDvarwcX',
  },
  GITHUB: {
    name: 'GitHub',
    url: 'https://github.com/DeOrderbook',
  },
}

export const DOB_APPS_MAP: {
  [key in DeOrderBookApp]: {
    name: string
    showGasInfo: boolean
    showConnectWallet: boolean
    showMenu: boolean
    showNetworkSelect: boolean
    showLaunchApp?: boolean
    showFaucetInfo?: boolean
    links: { name: string; url: string }[]
    externalLinks: { name: string; url: string }[]
  }
} = {
  main: {
    name: '',
    links: [
      {
        name: 'header.deorder',
        url: '/',
      },
      {
        name: 'header.pools',
        url: '/pools',
      },
      {
        name: 'header.lock-dob',
        url: '/lock',
      },
      {
        name: 'header.dashboard',
        url: '/dashboard',
      },
    ],
    showGasInfo: true,
    showConnectWallet: true,
    showMenu: true,
    showNetworkSelect: true,
    showFaucetInfo: true,
    externalLinks: [
      DOB_EXTERNAL_LINKS_MAP.BAZAAR,
      DOB_EXTERNAL_LINKS_MAP.EXPLORER,
      DOB_EXTERNAL_LINKS_MAP.STATS,
      DOB_EXTERNAL_LINKS_MAP.DOCS,
    ],
  },
  home: {
    name: '',
    showGasInfo: false,
    showConnectWallet: false,
    showMenu: false,
    showNetworkSelect: false,
    showLaunchApp: true,
    links: [],
    externalLinks: [
      DOB_EXTERNAL_LINKS_MAP.APP,
      DOB_EXTERNAL_LINKS_MAP.BAZAAR,
      DOB_EXTERNAL_LINKS_MAP.EXPLORER,
      DOB_EXTERNAL_LINKS_MAP.STATS,
    ],
  },
  pro: {
    name: 'Pro',
    links: [
      {
        name: 'Create Options',
        url: '/',
      },
      {
        name: 'Settings',
        url: '/settings',
      },
    ],
    showGasInfo: true,
    showConnectWallet: true,
    showMenu: true,
    showNetworkSelect: true,
    externalLinks: [
      DOB_EXTERNAL_LINKS_MAP.APP,
      DOB_EXTERNAL_LINKS_MAP.BAZAAR,
      DOB_EXTERNAL_LINKS_MAP.EXPLORER,
      DOB_EXTERNAL_LINKS_MAP.STATS,
      DOB_EXTERNAL_LINKS_MAP.DOCS,
    ],
  },
  bazaar: {
    name: 'Bazaar',
    links: [
      {
        name: 'My BULLET',
        url: '/',
      },
      {
        name: 'Buy BULLET',
        url: '/buy',
      },
    ],
    showGasInfo: true,
    showConnectWallet: true,
    showMenu: true,
    showNetworkSelect: true,
    externalLinks: [
      DOB_EXTERNAL_LINKS_MAP.APP,
      DOB_EXTERNAL_LINKS_MAP.EXPLORER,
      DOB_EXTERNAL_LINKS_MAP.STATS,
      DOB_EXTERNAL_LINKS_MAP.DOCS,
    ],
  },
  explorer: {
    name: 'Explorer',
    showGasInfo: false,
    showConnectWallet: false,
    showMenu: true,
    showNetworkSelect: true,
    links: [],
    externalLinks: [
      DOB_EXTERNAL_LINKS_MAP.APP,
      DOB_EXTERNAL_LINKS_MAP.BAZAAR,
      DOB_EXTERNAL_LINKS_MAP.STATS,
      DOB_EXTERNAL_LINKS_MAP.DOCS,
    ],
  },
  nft: {
    name: 'NFT',
    showGasInfo: true,
    showConnectWallet: true,
    showMenu: true,
    showNetworkSelect: true,
    links: [],
    externalLinks: [
      DOB_EXTERNAL_LINKS_MAP.APP,
      DOB_EXTERNAL_LINKS_MAP.BAZAAR,
      DOB_EXTERNAL_LINKS_MAP.EXPLORER,
      DOB_EXTERNAL_LINKS_MAP.STATS,
      DOB_EXTERNAL_LINKS_MAP.DOCS,
    ],
  },
  stats: {
    name: 'Stats',
    showGasInfo: false,
    showConnectWallet: false,
    showMenu: true,
    showNetworkSelect: true,
    links: [],
    externalLinks: [
      DOB_EXTERNAL_LINKS_MAP.APP,
      DOB_EXTERNAL_LINKS_MAP.BAZAAR,
      DOB_EXTERNAL_LINKS_MAP.EXPLORER,
      DOB_EXTERNAL_LINKS_MAP.DOCS,
    ],
  },
}

export const META_IMAGES = {
  LARGE: `${DOB_EXTERNAL_LINKS_MAP.HOME.url}/deorderbook-banner-large.webp`,
  SMALL: `${DOB_EXTERNAL_LINKS_MAP.HOME.url}/deorderbook-banner-small.webp`,
}
